/**
 * Export utilities for converting audit trail data to JSON and CSV formats.
 *
 * These functions produce downloadable string content from an
 * EvaluationAuditTrail structure.
 */

import type { EvaluationAuditTrail } from '@amzn/helianthus-arbiter-client';

// ---------------------------------------------------------------------------
// JSON export
// ---------------------------------------------------------------------------

/**
 * Convert an EvaluationAuditTrail to a pretty-printed JSON string.
 */
export function auditTrailToJson(auditTrail: EvaluationAuditTrail): string {
  return JSON.stringify(auditTrail, null, 2);
}

// ---------------------------------------------------------------------------
// CSV export
// ---------------------------------------------------------------------------

/**
 * Escape a CSV field value.
 *
 * - Wraps in quotes if it contains commas, quotes, or newlines.
 * - Prefixes with a single-quote to neutralize formula injection
 *   when the value starts with =, +, -, @, \t, or \r (Excel/Sheets
 *   treats these as formula triggers).
 */
function escapeCsvField(value: string): string {
  let sanitized = value;

  // Neutralize formula injection prefixes
  if (/^[=+\-@\t\r]/.test(sanitized)) {
    sanitized = `'${sanitized}`;
  }

  if (sanitized.includes(',') || sanitized.includes('"') || sanitized.includes('\n')) {
    return `"${sanitized.replace(/"/g, '""')}"`;
  }
  return sanitized;
}

/**
 * Convert an EvaluationAuditTrail to a CSV string.
 *
 * Produces one row per AuditEntry with columns:
 * evaluationId, entryType, timestamp, description, s3Url, fileSizeBytes
 */
export function auditTrailToCsv(auditTrail: EvaluationAuditTrail): string {
  const headers = ['evaluationId', 'entryType', 'timestamp', 'description', 's3Url', 'fileSizeBytes'];
  const rows: string[] = [headers.join(',')];

  const entries = auditTrail.auditEntries ?? [];
  for (const entry of entries) {
    const row = [
      escapeCsvField(auditTrail.evaluationId ?? ''),
      escapeCsvField(entry.entryType ?? ''),
      escapeCsvField(formatTimestampForCsv(entry.timestamp)),
      escapeCsvField(entry.description ?? ''),
      escapeCsvField(entry.s3Url ?? ''),
      escapeCsvField(String(entry.fileSizeBytes ?? '')),
    ];
    rows.push(row.join(','));
  }

  return rows.join('\n');
}

function formatTimestampForCsv(timestamp: Date | undefined): string {
  if (!timestamp) return '';
  if (typeof timestamp === 'string') {
    return timestamp;
  }
  return timestamp.toISOString();
}

// ---------------------------------------------------------------------------
// Download trigger
// ---------------------------------------------------------------------------

/**
 * Trigger a browser download of a string as a file.
 *
 * Creates a temporary anchor element, clicks it, and cleans up.
 */
export function downloadAsFile(content: string, filename: string, mimeType: string): void {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(url);
}
